import { useTranslation } from 'react-i18next';
import { flatNavItems } from '@/components/layout/nav.config';

/**
 * The arithmetic and wording the usage panels share.
 *
 * Kept in one place so a share is rounded the same way in the top-pages table
 * as in the breakdown beside it; two panels that disagree in the last digit
 * read as two different figures.
 */

export function ratio(top: number, bottom: number) {
  return bottom > 0 ? top / bottom : 0;
}

export function formatRate(value: number) {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
}

export function formatShare(fraction: number) {
  const percent = fraction * 100;
  // Under a tenth of a percent is still something, and "0%" would say it is not.
  if (percent > 0 && percent < 0.1) return '<0.1%';
  return `${percent.toFixed(1)}%`;
}

/** A date's `yyyy-MM-dd` on the reader's own calendar. */
export function localDay(date: Date) {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const NAV_LABELS = new Map(flatNavItems.map((item) => [item.key, item.label]));

/**
 * A page's name.
 *
 * Visits are stored as the client's own key for the page, so only this app's
 * keys can be labelled from here. Another app's key is shown as it was sent,
 * with the app named beside it wherever more than one can appear.
 */
export function usePageLabel() {
  const { t } = useTranslation();

  return (domain: string, page: string) => {
    const label = domain === 'admin-portal' ? NAV_LABELS.get(page) : null;
    return label ? t(label) : page;
  };
}

/** The app's name if there is one for it, and its slug if there is not. */
export function useDomainLabel() {
  const { t } = useTranslation();
  return (domain: string) =>
    t(`pageHistory.apps.${domain}`, { defaultValue: domain });
}
